/**
 * Commission Wallet Service
 *
 * API service for Commission Wallet operations (Module 6).
 * Handles wallet balance, transactions, add money, withdrawals, and analytics.
 *
 * Base URL: /api/leadflow/wallet
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

/**
 * Transaction type configurations
 */
export const TRANSACTION_TYPES = {
    credit: {
        value: 'credit',
        label: 'Money Added',
        icon: 'pi-plus-circle',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        severity: 'success'
    },
    debit: {
        value: 'debit',
        label: 'Money Spent',
        icon: 'pi-minus-circle',
        color: '#EF4444',
        bgColor: '#FEE2E2',
        severity: 'danger'
    },
    lock: {
        value: 'lock',
        label: 'Amount Locked',
        icon: 'pi-lock',
        color: '#F97316',
        bgColor: '#FFEDD5',
        severity: 'warning'
    },
    unlock: {
        value: 'unlock',
        label: 'Amount Unlocked',
        icon: 'pi-lock-open',
        color: '#3B82F6',
        bgColor: '#DBEAFE',
        severity: 'info'
    },
    transfer_in: {
        value: 'transfer_in',
        label: 'Received Transfer',
        icon: 'pi-arrow-down-left',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        severity: 'success'
    },
    transfer_out: {
        value: 'transfer_out',
        label: 'Sent Transfer',
        icon: 'pi-arrow-up-right',
        color: '#EF4444',
        bgColor: '#FEE2E2',
        severity: 'danger'
    }
};

/**
 * Analytics period options
 */
export const ANALYTICS_PERIODS = [
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'year', label: 'Last Year' }
];

/**
 * Transaction filter options
 */
export const TRANSACTION_FILTER_OPTIONS = [
    { value: null, label: 'All Transactions' },
    { value: 'credit', label: 'Money Added' },
    { value: 'debit', label: 'Money Spent' },
    { value: 'lock', label: 'Amount Locked' },
    { value: 'unlock', label: 'Amount Unlocked' },
    { value: 'transfer_in', label: 'Received Transfers' },
    { value: 'transfer_out', label: 'Sent Transfers' }
];

/**
 * Export format options
 */
export const EXPORT_FORMATS = [
    { value: 'csv', label: 'CSV', icon: 'pi-file' },
    { value: 'json', label: 'JSON', icon: 'pi-code' }
];

// ================================
// API SERVICE METHODS
// ================================

export const walletService = {
    /**
     * Get wallet balance and details
     * @returns {Promise<Object>} Wallet details with balances and stats
     */
    getWallet: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/wallet');
            return response.data;
        } catch (error) {
            console.error('walletService.getWallet:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get transaction history with filters
     * @param {Object} filters - Filter parameters
     * @param {string|string[]} filters.type - Transaction type(s)
     * @param {string} filters.date_from - Start date (YYYY-MM-DD)
     * @param {string} filters.date_to - End date (YYYY-MM-DD)
     * @param {number} filters.min_amount - Minimum amount
     * @param {number} filters.max_amount - Maximum amount
     * @param {string} filters.reference_type - Reference type filter
     * @param {number} filters.page - Page number
     * @param {number} filters.per_page - Items per page (max 100)
     * @returns {Promise<Object>} Paginated transactions
     */
    getTransactions: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Handle all filter types
            const filterKeys = [
                'type', 'date_from', 'date_to',
                'min_amount', 'max_amount', 'reference_type',
                'page', 'per_page'
            ];

            filterKeys.forEach(key => {
                const value = filters[key];
                if (value !== null && value !== undefined && value !== '') {
                    // Handle array values (e.g., multiple types)
                    if (Array.isArray(value)) {
                        value.forEach(v => params.append(`${key}[]`, v));
                    } else {
                        params.append(key, value);
                    }
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/wallet/transactions?${query}` : '/leadflow/wallet/transactions';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('walletService.getTransactions:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get single transaction details
     * @param {number} transactionId - Transaction ID
     * @returns {Promise<Object>} Transaction details with metadata
     */
    getTransactionDetails: async (transactionId) => {
        try {
            const response = await axiosInstance.get(`/leadflow/wallet/transactions/${transactionId}`);
            return response.data;
        } catch (error) {
            console.error('walletService.getTransactionDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get wallet analytics
     * @param {string} period - 'week', 'month', 'quarter', 'year'
     * @returns {Promise<Object>} Analytics data with charts
     */
    getAnalytics: async (period = 'month') => {
        try {
            const response = await axiosInstance.get(`/leadflow/wallet/analytics?period=${period}`);
            return response.data;
        } catch (error) {
            console.error('walletService.getAnalytics:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Create order for adding money (Razorpay)
     * @param {number} amount - Amount in INR (min: 100, max: 100000)
     * @returns {Promise<Object>} Order details for payment
     */
    createAddMoneyOrder: async (amount) => {
        try {
            const response = await axiosInstance.post('/leadflow/wallet/add-money', { amount });
            return response.data;
        } catch (error) {
            console.error('walletService.createAddMoneyOrder:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Generate payment token for cross-tenant redirect
     * @param {Object} orderData - Order data from createAddMoneyOrder
     * @param {string} orderData.order_id - Razorpay order ID
     * @param {string} orderData.payment_confirm_token - Payment confirmation token
     * @returns {Promise<Object>} Payment URL and token for redirect
     */
    generatePaymentToken: async (orderData) => {
        try {
            const response = await axiosInstance.post('/leadflow/wallet/add-money/generate-token', {
                order_id: orderData.order_id,
                source_domain: window.location.hostname,
                source_path: '/app/leadflow/wallet',
                payment_confirm_token: orderData.payment_confirm_token
            });
            return response.data;
        } catch (error) {
            console.error('walletService.generatePaymentToken:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Verify payment and add money to wallet
     * @param {Object} paymentData - Payment verification data
     * @param {string} paymentData.order_id - Razorpay order ID
     * @param {string} paymentData.payment_id - Razorpay payment ID
     * @param {string} paymentData.signature - Razorpay signature
     * @returns {Promise<Object>} Result with new balance
     */
    verifyAddMoney: async (paymentData) => {
        try {
            const response = await axiosInstance.post('/leadflow/wallet/add-money/verify', {
                order_id: paymentData.order_id,
                payment_id: paymentData.payment_id,
                signature: paymentData.signature
            });
            return response.data;
        } catch (error) {
            console.error('walletService.verifyAddMoney:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Request withdrawal to bank account
     * @param {Object} withdrawalData - Withdrawal request data
     * @param {number} withdrawalData.amount - Amount in INR (min: 500)
     * @param {Object} withdrawalData.bank_details - Bank details (optional)
     * @returns {Promise<Object>} Withdrawal request result
     */
    requestWithdrawal: async (withdrawalData) => {
        try {
            const payload = {
                amount: withdrawalData.amount
            };

            if (withdrawalData.bank_details) {
                payload.bank_details = withdrawalData.bank_details;
            }

            const response = await axiosInstance.post('/leadflow/wallet/withdraw', payload);
            return response.data;
        } catch (error) {
            console.error('walletService.requestWithdrawal:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Export transactions
     * @param {Object} options - Export options
     * @param {string} options.format - 'csv' or 'json'
     * @param {string} options.date_from - Start date
     * @param {string} options.date_to - End date
     * @param {string} options.type - Transaction type filter
     * @returns {Promise<Object|Blob>} Export data or file blob
     */
    exportTransactions: async (options = {}) => {
        try {
            const params = new URLSearchParams();
            const format = options.format || 'csv';
            params.append('format', format);

            if (options.date_from) params.append('date_from', options.date_from);
            if (options.date_to) params.append('date_to', options.date_to);
            if (options.type) params.append('type', options.type);

            const url = `/leadflow/wallet/export?${params.toString()}`;

            // For CSV, we expect blob response
            if (format === 'csv') {
                const response = await axiosInstance.get(url, {
                    responseType: 'blob'
                });
                return response.data;
            }

            // For JSON, return normal response
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('walletService.exportTransactions:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Download transactions as file
     * @param {Object} options - Export options
     */
    downloadTransactions: async (options = {}) => {
        try {
            const format = options.format || 'csv';
            const data = await walletService.exportTransactions(options);

            if (format === 'csv') {
                // Create blob and download
                const blob = new Blob([data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `wallet_transactions_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // JSON download
                const jsonStr = JSON.stringify(data.data, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = data.filename || `wallet_transactions_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('walletService.downloadTransactions:', error);
            throw error;
        }
    }
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get transaction type configuration
 * @param {string} type - Transaction type
 * @returns {Object} Type configuration
 */
export const getTransactionTypeConfig = (type) => {
    return TRANSACTION_TYPES[type] || {
        value: type,
        label: type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        icon: 'pi-question',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        severity: 'secondary'
    };
};

/**
 * Format currency in Indian format
 * @param {number} amount - Amount in INR
 * @param {boolean} showDecimals - Show decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showDecimals = true) => {
    if (amount === null || amount === undefined) return '-';

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0
    }).format(amount);
};

/**
 * Format currency with color based on transaction type
 * @param {number} amount - Amount
 * @param {boolean} isIncoming - True if money is coming in
 * @returns {Object} Formatted amount with color
 */
export const formatTransactionAmount = (amount, isIncoming) => {
    const prefix = isIncoming ? '+' : '-';
    const color = isIncoming ? '#22C55E' : '#EF4444';
    const formattedAmount = formatCurrency(Math.abs(amount));

    return {
        display: `${prefix}${formattedAmount}`,
        color,
        prefix,
        amount: formattedAmount
    };
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format relative time
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * Calculate percentage change
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {Object} Change data
 */
export const calculateChange = (current, previous) => {
    if (!previous || previous === 0) {
        return {
            percentage: current > 0 ? 100 : 0,
            direction: current > 0 ? 'up' : 'neutral',
            isPositive: current > 0
        };
    }

    const change = ((current - previous) / Math.abs(previous)) * 100;
    return {
        percentage: Math.abs(change).toFixed(1),
        direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
        isPositive: change >= 0
    };
};

/**
 * Get wallet health status based on balance
 * @param {Object} wallet - Wallet data
 * @returns {Object} Health status
 */
export const getWalletHealth = (wallet) => {
    const balance = wallet?.available_balance || 0;
    const lockedRatio = wallet?.locked_balance ? wallet.locked_balance / (balance + wallet.locked_balance) : 0;

    if (balance >= 10000) {
        return {
            status: 'healthy',
            label: 'Healthy',
            color: '#22C55E',
            icon: 'pi-check-circle'
        };
    } else if (balance >= 1000) {
        return {
            status: 'moderate',
            label: 'Moderate',
            color: '#EAB308',
            icon: 'pi-exclamation-circle'
        };
    } else {
        return {
            status: 'low',
            label: 'Low Balance',
            color: '#EF4444',
            icon: 'pi-exclamation-triangle'
        };
    }
};

/**
 * Prepare chart data for analytics
 * @param {Array} data - Array of {date, amount} objects
 * @param {string} label - Dataset label
 * @param {string} color - Line color
 * @returns {Object} Chart.js compatible data
 */
export const prepareChartData = (data, label, color) => {
    if (!data || !Array.isArray(data)) return null;

    return {
        labels: data.map(d => {
            const date = new Date(d.date);
            return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
        }),
        datasets: [{
            label,
            data: data.map(d => d.amount),
            borderColor: color,
            backgroundColor: `${color}20`,
            fill: true,
            tension: 0.4
        }]
    };
};

/**
 * Prepare breakdown data for pie/doughnut chart
 * @param {Array} data - Array of {source, total} objects
 * @returns {Object} Chart.js compatible data
 */
export const prepareBreakdownChart = (data) => {
    if (!data || !Array.isArray(data)) return null;

    const colors = ['#3B82F6', '#22C55E', '#EAB308', '#F97316', '#EF4444', '#8B5CF6'];

    return {
        labels: data.map(d => d.source || 'Other'),
        datasets: [{
            data: data.map(d => d.total),
            backgroundColor: data.map((_, i) => colors[i % colors.length]),
            borderWidth: 0
        }]
    };
};

export default walletService;
